import {
  PermissionsAction,
  PermissionsPosModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import type {
  ApprovalFlow,
  ApprovalStep
} from "@/components/Contracts/contract.interface";
import { IStep } from "@/modules/Terms/interfaces/ITerm.interface";

export const contractsCache = [];

interface IBuildContractStatusActions {
  contract: any;
  approvalFlow?: ApprovalFlow;
  permissions: any;
  nextStep: IStep;
  isButtonDisabled: boolean;
  action: string;
  toggleActionModal: (action?: string) => void;
  setAction: (action: string) => void;
  setActionMessage: (message: string) => void;
}

export function buildContractStatusActions({
  contract,
  approvalFlow,
  permissions,
  nextStep,
  isButtonDisabled,
  action,
  toggleActionModal,
  setAction,
  setActionMessage
}: IBuildContractStatusActions) {
  const status = [];

  const getStepByInternalName = (name: string) =>
    approvalFlow?.steps?.find(step => step.internalName === name);

  const rejectedAction = getStepByInternalName("REJECTED");
  const canceledAction = getStepByInternalName("CANCELED");
  const deliveredAction = getStepByInternalName("SERVICE_DELIVERED");
  const doneAction = getStepByInternalName("DONE");

  const hasPermissionForNextStepAction = permissions[PermissionsScope.POS]?.[
    PermissionsPosModule.CONTRACTS
  ]?.some(
    contractAction =>
      contractAction === PermissionsAction.ALL ||
      contractAction === nextStep?.interactionAction
  );

  const canExecuteNextStep =
    nextStep &&
    nextStep.interactionType === "ACTION_STATUS" &&
    hasPermissionForNextStepAction;

  if (canExecuteNextStep) {
    const handleNextStepClick = () => {
      toggleActionModal();

      const { interactionAction, interactionName } = nextStep;

      const isNewAction = interactionAction !== action;

      if (isNewAction) {
        setAction(interactionAction);
        setActionMessage(interactionName);
      }
    };

    status.push({
      key: nextStep.id,
      text: nextStep.interactionName,
      onClick: handleNextStepClick,
      id: `nextStep-${nextStep?.internalName}-button`.replace(/\s/g, ""),
      disabled: isButtonDisabled
    });
  }

  const contractsStatus = contractsCache?.flatMap(value => value);
  const alreadyHandled = contractsStatus
    .map(value => value?.id)
    .includes(contract?.id);

  let newStatus = status;

  if (alreadyHandled) {
    newStatus = status.filter(
      item =>
        item.text !== "Cancelar Contrato" && item.text !== "Rejeitar Contrato"
    );
  }

  function createStatusButton(stepAction?: ApprovalStep) {
    const hasPermissionForStepAction = permissions[PermissionsScope.POS]?.[
      PermissionsPosModule.CONTRACTS
    ]?.some(
      contractAction =>
        contractAction === PermissionsAction.ALL ||
        contractAction === stepAction?.interactionAction
    );

    const userCanExecuteStepAction =
      nextStep && stepAction && hasPermissionForStepAction;

    if (userCanExecuteStepAction) {
      return {
        key: stepAction.id,
        id: `nextStep-${stepAction.internalName}-button`.replace(/\s/g, "-"),
        text: stepAction.interactionName,
        onClick: () => {
          toggleActionModal(stepAction.interactionAction);

          if (stepAction.interactionAction !== action) {
            setAction(stepAction.interactionAction);
            setActionMessage(stepAction.interactionName);
          }
        }
      };
    }

    return null;
  }

  const productHasBeenDelivered = nextStep?.order >= deliveredAction?.order;
  const canceledButton = createStatusButton(canceledAction);

  if (canceledButton && !productHasBeenDelivered)
    newStatus.push(canceledButton);

  const contractIsDone = nextStep?.order >= doneAction?.order;
  const rejectedButton = createStatusButton(rejectedAction);

  if (rejectedButton && !contractIsDone) newStatus.push(rejectedButton);

  return newStatus;
}
